import { StyleSheet, Platform } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';
import type { ThemeMode } from '../../../src/theme/ThemeContext';

export const getGroupDetailViewStyles = (
  theme: Theme,
  themeMode: ThemeMode,
  windowWidth = 390,
  bottomInset = 0,
  reserveSelfBar = false,
  isTvMode = false,
) => {
  const isDark = themeMode === 'dark';
  const wcWebMaxWidth = Platform.OS === 'web' && !isTvMode
    ? (windowWidth >= 1024 ? 880 : windowWidth >= 768 ? 700 : 560)
    : undefined;

  // Clear the floating "your standing" bar (≈62px tall above the inset) so the
  // last row / footer pin is never hidden behind it.
  const selfBarReservedSpace = reserveSelfBar ? bottomInset + 90 : 100;

  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.background.primary,
    },
    scrollContent: {
      paddingBottom: selfBarReservedSpace,
      ...(wcWebMaxWidth != null && {
        maxWidth: wcWebMaxWidth,
        width: '100%' as never,
        alignSelf: 'center' as const,
      }),
    },
    // Back button used only in the empty/error state (banner has its own)
    backButton: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingHorizontal: 16,
      paddingVertical: 10,
      alignSelf: 'flex-start',
    },
    backButtonPressed: { opacity: 0.7 },
    backButtonHover: { opacity: 0.85 },
    backLabel: {
      fontSize: 14,
      fontWeight: '600',
      color: theme.primary,
    },
    // ── Section header (Leaderboard title) ───────────────────────────────────
    sectionHeader: {
      paddingHorizontal: 16,
      marginBottom: 8,
    },
    sectionTitle: {
      fontSize: 14,
      fontWeight: '800',
      letterSpacing: 0.8,
      color: theme.gray[800],
    },
    searchOuter: {
      marginHorizontal: 16,
      marginBottom: 10,
    },
    searchField: {
      flexDirection: 'row',
      alignItems: 'center',
      minHeight: 40,
      paddingHorizontal: 10,
      paddingVertical: 2,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: isDark ? theme.primary + '55' : theme.gray[300],
      backgroundColor: theme.background.card,
      ...(Platform.OS === 'web' && {
        boxShadow: isDark
          ? (`0 1px 3px rgba(0,0,0,0.35), 0 0 0 1px ${theme.primary}33` as never)
          : (`0 1px 4px rgba(0,0,0,0.06), 0 0 0 1px ${theme.primary}22` as never),
      }),
      ...(Platform.OS !== 'web' && {
        shadowColor: theme.shadow,
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: isDark ? 0.35 : 0.12,
        shadowRadius: isDark ? 4 : 5,
        elevation: isDark ? 3 : 2,
      }),
    },
    searchFieldFocused: {
      borderColor: theme.primary,
      ...(Platform.OS === 'web' && {
        boxShadow: isDark
          ? (`0 2px 8px rgba(0,0,0,0.4), 0 0 0 2px ${theme.primary}66` as never)
          : (`0 2px 10px rgba(107,70,193,0.18), 0 0 0 2px ${theme.primary}55` as never),
      }),
      ...(Platform.OS !== 'web' && {
        shadowOpacity: isDark ? 0.45 : 0.18,
        shadowRadius: isDark ? 6 : 8,
        elevation: isDark ? 5 : 4,
      }),
    },
    searchIconSlot: {
      marginRight: 2,
    },
    searchInput: {
      flex: 1,
      minHeight: Platform.OS === 'android' ? 36 : 38,
      paddingVertical: Platform.OS === 'ios' ? 8 : 4,
      paddingHorizontal: 0,
      margin: 0,
      fontSize: 14,
      fontWeight: '500',
      letterSpacing: 0.15,
      color: theme.gray[900],
      backgroundColor: 'transparent',
      ...(Platform.OS === 'web' && {
        outlineStyle: 'none' as never,
        outlineWidth: 0,
        boxShadow: 'none' as never,
        fontSize: 16, // prevents iOS Safari auto-zoom on focus (requires ≥16px)
      }),
    },
    searchEmptyState: {
      marginHorizontal: 16,
      borderWidth: 2,
      borderColor: isDark ? theme.primary + '44' : theme.gray[200],
      borderRadius: 14,
      paddingVertical: 16,
      paddingHorizontal: 14,
      backgroundColor: isDark ? theme.background.cardElevated : theme.background.card,
      marginBottom: 8,
      ...(Platform.OS === 'web' && {
        boxShadow: isDark
          ? (`0 2px 14px rgba(0,0,0,0.35)` as never)
          : (`0 2px 10px rgba(0,0,0,0.05)` as never),
      }),
    },
    searchEmptyLabel: {
      fontSize: 14,
      fontWeight: '600',
      color: theme.gray[600],
      textAlign: 'center',
    },
    futuresHiddenBanner: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 7,
      marginHorizontal: 16,
      marginBottom: 8,
      paddingVertical: 8,
      paddingHorizontal: 10,
      borderRadius: 10,
      backgroundColor: theme.background.inset,
    },
    futuresHiddenBannerText: {
      flex: 1,
      fontSize: 12,
      fontWeight: '600',
      color: theme.gray[600],
      lineHeight: 16,
    },
    // ── Empty state ───────────────────────────────────────────────────────────
    emptyState: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 32,
      paddingBottom: 48,
    },
    emptyMonkey: {
      width: 100,
      height: 100,
      marginBottom: 16,
    },
    emptyTitle: {
      fontSize: 18,
      fontWeight: '700',
      color: theme.gray[700],
      marginBottom: 8,
      textAlign: 'center',
    },
    emptySubtitle: {
      fontSize: 14,
      color: theme.gray[400],
      textAlign: 'center',
      lineHeight: 20,
    },
    // ── Error state ──────────────────────────────────────────────────────────
    errorContainer: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 32,
      paddingBottom: 48,
      backgroundColor: theme.background.primary,
    },
    errorIconWrap: {
      marginBottom: 16,
    },
    errorTitle: {
      fontSize: 18,
      fontWeight: '700',
      color: theme.gray[700],
      marginBottom: 8,
      textAlign: 'center',
    },
    errorSubtitle: {
      fontSize: 14,
      color: theme.gray[400],
      textAlign: 'center',
      lineHeight: 20,
      marginBottom: 24,
    },
    errorButton: {
      paddingHorizontal: 24,
      paddingVertical: 10,
      borderRadius: 20,
      backgroundColor: theme.primary,
    },
    errorButtonLabel: {
      fontSize: 14,
      fontWeight: '700',
      color: theme.onPrimary,
    },
    tvRoot: {
      flex: 1,
      backgroundColor: theme.background.primary,
    },
    tvPodiumSection: {
      paddingBottom: 8,
      width: '100%' as never,
    },
    tvScroll: {
      flex: 1,
    },
    tvScrollContent: {
      paddingBottom: 24,
      ...(Platform.OS === 'web' && {
        width: '100%' as never,
        maxWidth: '100%' as never,
      }),
    },
  });
};
